import { defineStore } from 'pinia';
import { ref } from 'vue';
import { STORAGE_NAMESPACES } from '@/utils/constants';
import { ContactModel } from '@/models/ContactModel';
import { getContactTransactions } from '../services/HceTransactionApiService';

const LOCAL_STORAGE_KEY = `${STORAGE_NAMESPACES.HCE}:txHomeTransactions`;

export const useHceHomeTransactionsStore = defineStore(LOCAL_STORAGE_KEY,() => {
  const contactId = ref(null);
  const contact = ref(null);
  const transactions = ref([]);

  /**
   * Fetches contact and transaction data for a given contact ID.
   * 
   * This function retrieves both the contact's transaction data and the contact's
   * details concurrently. 
   * 
   * @async
   * @function fetchContactAndTransactions
   * @param {string|number} contactIdValue - The unique identifier of the contact.
   * @returns {Promise<void>} A promise that resolves once the contact and transaction data have been fetched and assigned.
   */
  async function fetchContactAndTransactions(contactIdValue) {
    contactId.value = contactIdValue;
    const [transactionsData, contactData] = await Promise.all([
      getContactTransactions(contactIdValue),
      ContactModel.getContact(contactIdValue),
    ]);
    
    transactions.value = transactionsData;
    contact.value = contactData;
  }

  /**
   * Refreshes the contact information by fetching the contact details
   * based on the current contact ID. If a valid contact ID is present,
   * it retrieves the contact data and updates the `contact` value.
   *
   * @async
   * @function
   * @returns {Promise<void>} Resolves when the contact information is updated.
   */
  async function refreshContact() {
    if (contactId.value) {
      contact.value = await ContactModel.getContact(contactId.value);
    }
  }

  /**
   * Refreshes the transactions for the current contact.
   * If a valid contact ID is available, it fetches the transactions
   * associated with that contact and updates the transactions value.
   *
   * @async
   * @function
   * @returns {Promise<void>} Resolves when the transactions are successfully refreshed.
   */
  async function refreshTransactions() {
    if (contactId.value) {
      transactions.value = await getContactTransactions(contactId.value);
    }
  }
  
  return {
    contactId,
    contact,
    transactions,
    fetchContactAndTransactions,
    refreshTransactions,
    refreshContact,
  };
});